import { useEffect, useSyncExternalStore, type ReactNode } from "react";

/**
 * Command palette registry. Screens can add context commands while mounted:
 *
 *   useRegisterCommands("Deployments", [
 *     { id: "deploy-web", label: "Deploy web to production", icon: <Rocket />, run: () => … },
 *   ], [deps]);
 */
export interface Command {
  id: string;
  label: string;
  /** Extra words to match on. */
  keywords?: string[];
  icon?: ReactNode;
  /** Right-aligned hint, e.g. a shortcut or "Project". */
  hint?: ReactNode;
  run: () => void;
}

interface Group {
  key: string;
  heading: string;
  commands: Command[];
}

let groups: Group[] = [];
const subs = new Set<() => void>();
const emit = () => subs.forEach((s) => s());

export function registerCommands(heading: string, commands: Command[]): () => void {
  const key = `${heading}-${Math.random().toString(36).slice(2)}`;
  groups = [...groups, { key, heading, commands }];
  emit();
  return () => {
    groups = groups.filter((g) => g.key !== key);
    emit();
  };
}

export function useRegisterCommands(heading: string, commands: Command[], deps: unknown[] = []): void {
  useEffect(() => registerCommands(heading, commands), deps); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useRegisteredCommands(): Group[] {
  return useSyncExternalStore(
    (l) => {
      subs.add(l);
      return () => subs.delete(l);
    },
    () => groups,
  );
}

/** Open the palette from anywhere. */
export const OPEN_PALETTE_EVENT = "guv:palette";
export function openCommandPalette(): void {
  window.dispatchEvent(new Event(OPEN_PALETTE_EVENT));
}
